import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Users,
  Server,
  CreditCard,
  TrendingUp,
  AlertTriangle,
  Clock,
  BarChart3,
  DollarSign,
  UserCheck,
  UserX,
  Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = "text-indigo-400", bg = "bg-indigo-500/10" }) => (
  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
        <Icon className={color} size={20} />
      </div>
      <span className="text-sm text-slate-400">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const AdminStats = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-500" size={32} />
      </div>
    );
  }

  if (!stats) return null;

  const maxReg = Math.max(...(stats.registrations_by_day || []).map((d) => d.count), 1);
  const maxRev = Math.max(...(stats.revenue_by_month || []).map((d) => d.amount), 1);

  return (
    <div className="space-y-8" data-testid="admin-stats-page">
      <h1 className="text-2xl font-bold text-white">Tableau de bord Admin</h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Utilisateurs" value={stats.users.total} sub={`${stats.users.active} actifs, ${stats.users.trial} en essai`} />
        <StatCard icon={DollarSign} label="MRR" value={`${stats.revenue.mrr} EUR`} sub="Revenu mensuel recurrent" color="text-emerald-400" bg="bg-emerald-500/10" />
        <StatCard icon={TrendingUp} label="Churn" value={`${stats.revenue.churn_rate}%`} sub="Taux d'annulation 30j" color={stats.revenue.churn_rate > 5 ? "text-red-400" : "text-emerald-400"} bg={stats.revenue.churn_rate > 5 ? "bg-red-500/10" : "bg-emerald-500/10"} />
        <StatCard icon={CreditCard} label="Revenu total" value={`${stats.revenue.total} EUR`} sub={`${stats.revenue.transactions} transactions`} color="text-amber-400" bg="bg-amber-500/10" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Server} label="Serveurs" value={stats.resources.servers} />
        <StatCard icon={UserCheck} label="Abonnements actifs" value={stats.users.active} color="text-emerald-400" bg="bg-emerald-500/10" />
        <StatCard icon={Clock} label="Essais expirant" value={stats.expiring_trials} sub="Dans les 7 prochains jours" color="text-amber-400" bg="bg-amber-500/10" />
        <StatCard icon={UserX} label="Suspendus" value={stats.users.suspended || 0} sub={`${stats.users.cancelled || 0} annules`} color="text-red-400" bg="bg-red-500/10" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan distribution */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6" data-testid="plan-distribution">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <BarChart3 size={16} /> Repartition des plans
          </h3>
          <div className="space-y-3">
            {(stats.plan_distribution || []).map((p) => {
              const total = (stats.plan_distribution || []).reduce((s, x) => s + x.count, 0);
              const pct = total > 0 ? Math.round((p.count / total) * 100) : 0;
              return (
                <div key={p.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-300">{p.name}</span>
                    <span className="text-sm text-slate-500">{p.count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="h-2 rounded-full bg-indigo-500" style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                </div>
              );
            })}
            {(!stats.plan_distribution || stats.plan_distribution.length === 0) && (
              <p className="text-slate-500 text-sm">Aucun plan actif</p>
            )}
          </div>
        </div>

        {/* Revenue by month */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6" data-testid="revenue-chart">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <DollarSign size={16} /> Revenu par mois
          </h3>
          <div className="flex items-end gap-2 h-40">
            {(stats.revenue_by_month || []).map((m, i) => {
              const height = maxRev > 0 ? Math.max((m.amount / maxRev) * 100, 4) : 4;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-slate-500">{m.amount > 0 ? `${m.amount}` : ""}</span>
                  <div
                    className="w-full bg-emerald-500/60 rounded-t transition-all hover:bg-emerald-500"
                    style={{ height: `${height}%` }}
                  />
                  <span className="text-[10px] text-slate-600">{m.month}</span>
                </div>
              );
            })}
            {(!stats.revenue_by_month || stats.revenue_by_month.length === 0) && (
              <p className="text-slate-500 text-sm">Pas de donnees</p>
            )}
          </div>
        </div>
      </div>

      {/* Registrations chart */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6" data-testid="registrations-chart">
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Users size={16} /> Inscriptions (30 derniers jours)
        </h3>
        <div className="flex items-end gap-px h-24">
          {(stats.registrations_by_day || []).map((d, i) => {
            const height = maxReg > 0 ? Math.max((d.count / maxReg) * 100, 2) : 2;
            return (
              <div key={i} className="flex-1 group relative">
                <div
                  className="w-full bg-indigo-500/40 rounded-t hover:bg-indigo-500 transition-all"
                  style={{ height: `${height}%` }}
                />
                <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 bg-slate-700 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                  {d.date}: {d.count}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-slate-600">{stats.registrations_by_day?.[0]?.date}</span>
          <span className="text-[10px] text-slate-600">{stats.registrations_by_day?.[stats.registrations_by_day.length - 1]?.date}</span>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
